import { Icon } from "@qw/ui";
import { type ReactNode, useMemo } from "react";

import type { EmptyStateAction } from "../components";
import { classifyInsuredOpportunities, type InsuredOpportunityLike } from "../insured-opportunity.helpers";

import type { UseMcpChatReturn } from "./use-mcp-chat";

export interface InsuredOpportunityEmptyStateProps {
  actions?: EmptyStateAction[];
  description: ReactNode;
}

interface UseInsuredOpportunityEmptyStatePropsOptions<TOpportunity extends InsuredOpportunityLike> {
  opportunities?: readonly TOpportunity[] | null;
  sendMessage: UseMcpChatReturn["sendMessage"];
}

export const useInsuredOpportunityEmptyStateProps = <TOpportunity extends InsuredOpportunityLike>({
  opportunities,
  sendMessage,
}: UseInsuredOpportunityEmptyStatePropsOptions<TOpportunity>): InsuredOpportunityEmptyStateProps | undefined => {
  const { losts, renewals, wons } = useMemo(() => classifyInsuredOpportunities({ opportunities }), [opportunities]);

  return useMemo(() => {
    if (renewals.length > 0) {
      return {
        actions: [
          {
            icon: <Icon icon="document_scanner" />,
            label: "Review renewal opportunities",
            onClick: () => sendMessage({ text: "review renewal opportunities for insured" }),
          },
        ],
        description: (
          <>
            I can see there {renewals.length === 1 ? "is 1 renewal" : `are ${renewals.length} renewals`} coming up.
            Would you like me to help with the renewal process?
          </>
        ),
      };
    }

    if (wons.length > 0) {
      return {
        actions: [
          {
            icon: <Icon icon="analytics" />,
            label: "Review supplemental forms",
            onClick: () =>
              sendMessage({ text: "what are the supplemental forms for the insured that have not been completed" }),
          },
        ],
        description: (
          <>
            Congratulations! There {wons.length === 1 ? "is 1 won opportunity" : `are ${wons.length} won opportunities`}
            . Would you like me to help with the next steps?
          </>
        ),
      };
    }

    if (losts.length > 0) {
      return {
        description: (
          <>
            I can see there {losts.length === 1 ? "is 1 lost opportunity" : `are ${losts.length} lost opportunities`}.
            Would you like to review what happened?
          </>
        ),
      };
    }

    if ((opportunities?.length ?? 0) > 0) {
      return {
        actions: [
          {
            icon: <Icon icon="rate_review" />,
            label: "Create SOV",
            onClick: () =>
              sendMessage({
                text: "create SOV for insured using the files attached to the insured and any linked conversations",
              }),
          },
          {
            icon: <Icon icon="document_scanner" />,
            label: "Review Opportunities",
            onClick: () => sendMessage({ text: "review opportunities for insured based on the linked conversations" }),
          },
        ],
        description: (
          <>
            I can see there{" "}
            {opportunities?.length === 1 ? "is 1 opportunity" : `are ${opportunities?.length} opportunities`} available.
            Would you like me to help move them forward?
          </>
        ),
      };
    }

    return undefined;
  }, [losts.length, opportunities?.length, renewals.length, sendMessage, wons.length]);
};
